import { Button, Text, VStack } from '@chakra-ui/react';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { ReactElement, useCallback } from 'react';
import { ModalCaseMeNewsletterVariantProps, SharedModalProps } from 'types/Modal';
import { useCaseMe } from 'hooks/api/backend/marketing-modal/getCaseMeApi';
import { useNavigate } from 'react-router-dom';
import { SessionStorageKey, useSessionStorage } from 'hooks/useSessionStorage';
import { useUser } from 'utils/auth';
import { PublicRoutes } from 'types/Routes';
import { CloseIcon } from 'assets/icons/v2';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalCaseMeNewsletterVariantProps;
export const CaseMeModal = (props: Props): ReactElement | null => {
  const navigate = useNavigate();
  const { isShown: isOpen, handleClose: closeModal, onClose, id, onRedirect } = props;
  const { data: crmData, trackCtaClick } = useCaseMe({ id });
  const deferredUrlStorage = useSessionStorage<string>(SessionStorageKey.DEFERRED_URL);
  const { data: user } = useUser();

  const handleClose = useCallback((): void => {
    if (onClose) onClose();
    if (closeModal) closeModal();
  }, [onClose, closeModal]);

  if (!isOpen || !crmData) return null;

  const handleRedirect = (): void => {
    trackCtaClick();

    if (crmData?.redirectUrl && user) {
      navigate(crmData.redirectUrl);
    } else {
      deferredUrlStorage.setValue(crmData.redirectUrl);
      navigate(PublicRoutes.LOGIN);
      handleClose();
    }
    if (onRedirect) onRedirect();
  };

  return (
    <CustomModal isOpen={isOpen} onClose={handleClose} placement="center" size="sm" hideHeader>
      <CustomModalContent
        p={{ base: '0', md: '0 32px 32px' }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        position="relative"
      >
        <NovaIconButton
          aria-label="Schließen"
          variant="primary"
          size="l"
          onClick={handleClose}
          position="absolute"
          top="16px"
          right="16px"
          _hover={{ background: 'grey.100' }}
          icon={CloseIcon}
        />
        <VStack width="100%" gap={{ base: 'm', md: 'xl' }} mt={{ base: '3xl', md: '5xl' }}>
          <Text textStyle="h4.medium" color="text.accent" textAlign="center">
            {crmData.header}
          </Text>
          <Text textStyle="body.m.regular" color="text.primary" textAlign="center" marginTop="10px">
            {crmData.subheader}
          </Text>
          <Text
            textStyle="body.s.regular"
            color="text.secondary"
            textAlign="center"
            marginBottom="l"
          >
            {crmData.description}
          </Text>
          <Button
            variant="primary"
            backgroundColor="background.accent"
            onClick={handleRedirect}
            size="l"
          >
            {crmData.ctaLabel || 'Jetzt überprüfen'}
          </Button>
        </VStack>
      </CustomModalContent>
    </CustomModal>
  );
};
